import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faTimes } from '@fortawesome/free-solid-svg-icons';

import { getRecapInvoices, getRecapInvoicePDF } from '../services/invoicingService';
import './RecapInvoicesPanel.css';

/**
 * Approved outgoing invoices raised against this recap's deal, viewable as PDF.
 *
 * Read-only by design. Customer traders reach this because they own the recap,
 * not because they have the invoicing module -- they are still refused a 403
 * everywhere else in it. Every PDF served here is stamped "Copy" and never
 * spends the invoice's one Original.
 */
const RecapInvoicesPanel = ({ recapId }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  // The reason loading failed, in the server's own words. A caught error that
  // sets an empty list would render as "this deal has no invoices", which is a
  // different and wrong statement -- the exact silent-empty shape that made the
  // delivery invoice panel unreadable.
  const [loadError, setLoadError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfName, setPdfName] = useState('');
  const [openingId, setOpeningId] = useState(null);

  const load = useCallback(async () => {
    if (!recapId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getRecapInvoices(recapId);
      setInvoices(data.results || []);
    } catch (err) {
      setLoadError(
        err?.response?.data?.detail
        || err?.message
        || 'Could not load invoices for this recap.'
      );
    } finally {
      setLoading(false);
    }
  }, [recapId]);

  useEffect(() => { load(); }, [load]);

  // Revoke the object URL on unmount so closing the recap does not leak it.
  useEffect(() => () => { if (pdfUrl) window.URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  const openPdf = async (invoice) => {
    setOpeningId(invoice.id);
    try {
      const blob = await getRecapInvoicePDF(recapId, invoice.id);
      if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(window.URL.createObjectURL(blob));
      setPdfName(`Invoice_Copy_${invoice.invoice_number}.pdf`);
    } catch (err) {
      setLoadError(
        err?.response?.data?.detail || 'Could not open that invoice PDF.'
      );
    } finally {
      setOpeningId(null);
    }
  };

  const closePdf = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfName('');
  };

  if (!recapId) return null;

  return (
    <div className="recap-invoices-panel">
      <div className="recap-invoices-panel__header">
        <h2>Invoices</h2>
        <span className="recap-invoices-panel__note">
          Approved invoices for this deal. Copies only.
        </span>
      </div>

      {loading && <p className="recap-invoices-panel__state">Loading invoices…</p>}

      {!loading && loadError && (
        <div className="recap-invoices-panel__error" role="alert">
          <p>{loadError}</p>
          <button type="button" onClick={load}>Try again</button>
        </div>
      )}

      {!loading && !loadError && invoices.length === 0 && (
        <p className="recap-invoices-panel__state">
          No approved invoices have been raised against this deal yet.
        </p>
      )}

      {!loading && !loadError && invoices.length > 0 && (
        <table className="recap-invoices-panel__table">
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Date</th>
              <th>Entity</th>
              <th>Customer</th>
              <th aria-label="Open PDF" />
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id}>
                <td>{inv.invoice_number}</td>
                <td>{inv.invoice_date || '—'}</td>
                <td>{inv.hms_entity_name || '—'}</td>
                <td>{inv.partner_name || '—'}</td>
                <td>
                  <button
                    type="button"
                    onClick={() => openPdf(inv)}
                    disabled={openingId === inv.id}
                  >
                    <FontAwesomeIcon icon={faFilePdf} />{' '}
                    {openingId === inv.id ? 'Opening…' : 'View PDF'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pdfUrl && (
        <div className="recap-invoices-panel__viewer">
          <div className="recap-invoices-panel__viewer-bar">
            <span>{pdfName}</span>
            <button type="button" onClick={closePdf} aria-label="Close preview">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <iframe title={pdfName} src={pdfUrl} />
        </div>
      )}
    </div>
  );
};

export default RecapInvoicesPanel;
